import type { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import Axios from "axios";
import Cookies from "js-cookie";

import { toast } from "../hooks/use-toast";
import { env } from "../config/env";
import { paths } from "../config/paths";
import { ssrOriginInterceptor } from "../config/ssr-request-locals";

export interface RedirectResponse {
  redirect?: boolean;
  redirected?: boolean;
  url?: string;
}

export const apiV2 = Axios.create({
  baseURL: env.API_URL_V2,
  validateStatus: (status: number) => status >= 200 && status < 400,
});

export const publicApi = Axios.create({
  baseURL: env.API_URL_CATALOG,
  validateStatus: (status: number) => status >= 200 && status < 400,
});

export const authApi = Axios.create({
  baseURL: env.AUTH_API_URL,
  validateStatus: (status: number) => status >= 200 && status < 400,
});

export const authApiWithAuth = Axios.create({
  baseURL: env.AUTH_API_URL,
  validateStatus: (status: number) => status >= 200 && status < 400,
});

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  if (config.method && config.method.toUpperCase() !== "GET" && config.method.toUpperCase() !== "HEAD") {
    config.headers.set("ctoken", Cookies.get("ctoken"));
    if (config.data) {
      config.data = {
        ...config.data,
        _ctoken: Cookies.get("ctoken"),
      };
    }
  }
  return config;
}

function authResponseInterceptor(response: AxiosResponse) {
  if (isRedirect(response) && (response.redirect || response.redirected) && response.url) {
    window.location.href = response.url;
  }
  const requestUri = Axios.getUri(response.config);

  if (response.request?.responseURL && requestUri !== response.request.responseURL) {
    //redirected
    window.location.href = response.request.responseURL;
  }
  return response;
}

const isRedirect = (res: any): res is RedirectResponse => {
  return (
    Object.prototype.hasOwnProperty.call(res, "redirect") || Object.prototype.hasOwnProperty.call(res, "redirected")
  );
};

function authResponseErrorInterceptorFactory({ checkAuth }: { checkAuth: boolean }) {
  return function authResponseErrorInterceptor(error: any) {
    if (error.response?.status === 401 && checkAuth) {
      const searchParams = new URLSearchParams(window.location.search);
      const redirectTo =
        searchParams.get("redirectTo") || window.location.pathname + window.location.search + window.location.hash;
      window.location.href = paths.auth.login.getHref(redirectTo);
    }

    const message = error.response?.data?.message || error.response?.data?.error || error.message;
    console.error(error);
    // Server renders have no Toaster, and sonner's module-level queue would only grow.
    if (globalThis.window) {
      toast.error("Error", {
        description: message,
      });
    }

    return Promise.reject(error);
  };
}

for (const instance of [apiV2, publicApi, authApi, authApiWithAuth]) {
  instance.interceptors.request.use(ssrOriginInterceptor);
}

apiV2.interceptors.request.use(authRequestInterceptor);
apiV2.interceptors.response.use(authResponseInterceptor, authResponseErrorInterceptorFactory({ checkAuth: true }));

publicApi.interceptors.request.use(authRequestInterceptor);
publicApi.interceptors.response.use(authResponseInterceptor, authResponseErrorInterceptorFactory({ checkAuth: false }));

authApi.interceptors.request.use(authRequestInterceptor);

authApiWithAuth.interceptors.request.use(authRequestInterceptor);
authApiWithAuth.interceptors.response.use(authResponseInterceptor, authResponseErrorInterceptorFactory({ checkAuth: true }));